import { createClient } from '@supabase/supabase-js';

// Get environment variables - Vite automatically embeds these during build
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Validate configuration
if (!supabaseUrl || !supabaseAnonKey) {
  console.error('❌ Missing Supabase configuration');
  console.error('URL present:', !!supabaseUrl);
  console.error('Key present:', !!supabaseAnonKey);
  console.error('Environment check:', {
    VITE_SUPABASE_URL: import.meta.env.VITE_SUPABASE_URL ? 'SET' : 'MISSING',
    VITE_SUPABASE_ANON_KEY: import.meta.env.VITE_SUPABASE_ANON_KEY ? 'SET' : 'MISSING',
    NODE_ENV: import.meta.env.MODE
  });
} else {
  console.log('✅ Supabase configuration loaded:', {
    url: supabaseUrl.substring(0, 30) + '...',
    hasKey: !!supabaseAnonKey
  });
}

// Create Supabase client with native session persistence
// Note: Supabase JS client automatically sets correct Accept headers for PostgREST
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce'
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  },
  global: {
    headers: {
      'x-client-info': 'defera-web'
    }
  },
  db: {
    schema: 'public'
  }
});

// Test Supabase connection on initialization
if (supabaseUrl && supabaseAnonKey) {
  supabase.auth.getSession().then(({ data, error }) => {
    if (error) {
      console.error('❌ Supabase connection test failed:', error.message);
    } else {
      console.log('✅ Supabase client initialized successfully');
    }
  }).catch(err => {
    console.error('❌ Supabase initialization error:', err);
  });
}

// Setup auth state listener to keep defera.user in sync
supabase.auth.onAuthStateChange((event, session) => {
  console.log('🔔 Auth state changed:', event);
  
  if (event === 'INITIAL_SESSION') {
    console.log('✅ Initial session loaded from storage - no action needed');
  } else if (event === 'TOKEN_REFRESHED') {
    console.log('✅ Token refreshed successfully');
  } else if (event === 'SIGNED_OUT') {
    console.log('🔴 User signed out - clearing cached user data');
    localStorage.removeItem('defera.user');
  } else if (event === 'SIGNED_IN') {
    console.log('✅ User signed in');
  }
});

export const authHelpers = {
  async signUp(email, password, metadata = {}) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata
      }
    });
    return { data, error };
  },

  async signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    return { data, error };
  },

  async signOut() {
    const { error } = await supabase.auth.signOut();
    return { error };
  },

  async getCurrentUser() {
    const { data: { user }, error } = await supabase.auth.getUser();
    return { user, error };
  },

  async getSession() {
    const { data: { session }, error } = await supabase.auth.getSession();
    return { session, error };
  },

  onAuthStateChange(callback) {
    return supabase.auth.onAuthStateChange(callback);
  }
};

export const dbHelpers = {
  async createBooking(bookingData) {
    const { data, error } = await supabase
      .from('bookings')
      .insert(bookingData)
      .select()
      .single();
    return { data, error };
  },

  async getBookings(userId) {
    const { data, error } = await supabase
      .from('bookings')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    return { data, error };
  },

  async updateBooking(bookingId, updates) {
    const { data, error } = await supabase
      .from('bookings')
      .update(updates)
      .eq('id', bookingId)
      .select()
      .single();
    return { data, error };
  },

  async deleteBooking(bookingId) {
    const { error } = await supabase
      .from('bookings')
      .delete()
      .eq('id', bookingId);
    return { error };
  },

  async createPassenger(passengerData) {
    const { data, error } = await supabase
      .from('passengers')
      .insert(passengerData)
      .select()
      .single();
    return { data, error };
  },

  async getPassengers(userId) {
    const { data, error } = await supabase
      .from('passengers')
      .select('*')
      .eq('user_id', userId);
    return { data, error };
  },

  async updatePassenger(passengerId, updates) {
    const { data, error } = await supabase
      .from('passengers')
      .update(updates)
      .eq('id', passengerId)
      .select()
      .single();
    return { data, error };
  },

  async deletePassenger(passengerId) {
    const { error } = await supabase
      .from('passengers')
      .delete()
      .eq('id', passengerId);
    return { error };
  }
};

export default supabase;
